import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowRight,
    ArrowUpRight,
    BarChart3,
    Bell,
    Briefcase,
    Calculator,
    CalendarDays,
    FileText,
    Globe,
    IdCard,
    Inbox,
    LayoutGrid,
    Megaphone,
    Package,
    Receipt,
    RefreshCw,
    ShieldCheck,
    ShoppingCart,
    Smartphone,
    Sparkles,
    Users,
    Workflow as WorkflowIcon,
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const PER_PAGE = 4;

const CATEGORIES = [
    { id: "all", label: "All", icon: LayoutGrid },
    { id: "sales-marketing", label: "Sales & Marketing", icon: Users },
    { id: "ops-field", label: "Operations & Field", icon: CalendarDays },
    { id: "finance-commerce", label: "Finance & Commerce", icon: Calculator },
    { id: "stock-warehouse", label: "Stock & Warehouse", icon: Package },
    { id: "ai-intel", label: "AI & Intelligence", icon: Sparkles },
];

const MODULES = [
    {
        id: "job-management",
        cat: "ops-field",
        label: "Job Management",
        title: "Complete Visibility Across Every Job.",
        body: "Track every job from enquiry to invoice in real time, across your whole operation.",
        ai: "Highlights delays, suggests next actions and keeps customers informed.",
        cta: "Explore Job Management",
        icon: Briefcase,
        color: "#00E5FF",
    },
    {
        id: "scheduling",
        cat: "ops-field",
        label: "Scheduling & Dispatch",
        title: "The Right People. The Right Job. The Right Time.",
        body: "Plan and allocate work from one live schedule shared by office and field.",
        ai: "Assisted scheduling balances workloads, live traffic and optimisation.",
        cta: "Explore Scheduling & Dispatch",
        icon: CalendarDays,
        color: "#0066FF",
    },
    {
        id: "field",
        cat: "ops-field",
        label: "Field Operations",
        title: "Give Your Engineers Everything They Need.",
        body: "The TedOps Field app keeps teams connected with job details, forms, photos and customer information — online or offline.",
        ai: "Helps engineers capture complete, accurate data on site.",
        cta: "Explore TedOps Field",
        icon: Smartphone,
        color: "#7C5CFC",
    },
    {
        id: "hr-staff",
        cat: "ops-field",
        label: "HR & Staff Management",
        title: "Your Team, Organised.",
        body: "Staff records, holidays, absences, timesheets, training and documents in one place.",
        ai: "Spots cover gaps and absence clashes before they hurt the schedule.",
        cta: "Explore HR & Staff",
        icon: IdCard,
        color: "#D946EF",
    },
    {
        id: "health-safety",
        cat: "ops-field",
        label: "Health & Safety",
        title: "Compliance Without the Folder.",
        body: "Digital risk assessments, inspections, signatures and audit trails on every job.",
        ai: "Checks required forms are complete before work is signed off.",
        cta: "Explore Health & Safety",
        icon: ShieldCheck,
        color: "#00B8D9",
    },
    {
        id: "ai-insights",
        cat: "ai-intel",
        label: "AI Insights & Automation",
        title: "Smarter Decisions. Greater Efficiency.",
        body: "Turn your operational data into dashboards, suggested actions and automated workflows.",
        ai: "Identifies risks early and unlocks more revenue with less manual work.",
        cta: "Explore AI & Automation",
        icon: Sparkles,
        color: "#A855F7",
    },
    {
        id: "enquiries",
        cat: "sales-marketing",
        label: "Enquiries & CRM",
        title: "Never Miss an Opportunity.",
        body: "Capture every customer enquiry, note and opportunity in one place.",
        ai: "Qualifies and prioritises new opportunities as they arrive.",
        cta: "Explore Enquiries & CRM",
        icon: Inbox,
        color: "#00E5FF",
    },
    {
        id: "quotes",
        cat: "sales-marketing",
        label: "Quotes & Estimates",
        title: "Quote Fast. Win More Work.",
        body: "Build professional quotes in minutes and convert approved work straight into scheduled jobs.",
        ai: "Drafts quotes from similar past jobs for faster, consistent pricing.",
        cta: "Explore Quotes & Estimates",
        icon: FileText,
        color: "#0066FF",
    },
    {
        id: "marketing-automation",
        cat: "sales-marketing",
        label: "Marketing Automation",
        title: "Campaigns That Run Themselves.",
        body: "Targeted email campaigns, smart customer segments and automated journeys built from live customer data.",
        ai: "Builds segments and suggests the next best campaign from purchase history.",
        cta: "Explore Marketing Automation",
        icon: Megaphone,
        color: "#7C5CFC",
    },
    {
        id: "customer-comms",
        cat: "sales-marketing",
        label: "Customer Communications",
        title: "Customers Kept in the Loop.",
        body: "Automatic updates, reminders and appointment confirmations at every stage.",
        ai: "Times and tailors each message to the job's live status.",
        cta: "Explore Customer Comms",
        icon: Bell,
        color: "#D946EF",
    },
    {
        id: "stock",
        cat: "stock-warehouse",
        label: "Stock & Parts",
        title: "Every Part Accounted For.",
        body: "Live stock levels across depot and vans, tied to every job.",
        ai: "Predicts parts needed for upcoming work and flags reorder points early.",
        cta: "Explore Stock & Parts",
        icon: Package,
        color: "#A855F7",
    },
    {
        id: "purchasing",
        cat: "stock-warehouse",
        label: "Purchase Orders",
        title: "Purchasing Without the Paperwork.",
        body: "Raise purchase orders and track deliveries with supplier costs visible against every job.",
        ai: "Suggests order quantities from the work already scheduled.",
        cta: "Explore Purchasing",
        icon: ShoppingCart,
        color: "#D946EF",
    },
    {
        id: "invoicing",
        cat: "finance-commerce",
        label: "Invoicing & Payments",
        title: "Less Paperwork. Faster Payments.",
        body: "Completed work flows into accurate invoices with forms, photos and parts already attached.",
        ai: "Checks every job record for completeness before anything is billed.",
        cta: "Explore Invoicing",
        icon: Receipt,
        color: "#00E5FF",
    },
    {
        id: "finance-management",
        cat: "finance-commerce",
        label: "Finance Management",
        title: "Every Pound Accounted For.",
        body: "Bills, expenses, reimbursements and payments connected to the jobs and sales they belong to.",
        ai: "Flags margin leaks and unusual costs before the month-end surprises you.",
        cta: "Explore Finance Management",
        icon: Calculator,
        color: "#0066FF",
    },
    {
        id: "ecommerce-orders",
        cat: "finance-commerce",
        label: "E-commerce Orders",
        title: "Online and Offline, One Flow.",
        body: "Web orders import, pick, dispatch and invoice alongside everything else you sell.",
        ai: "Predicts demand from order history so stock is ready before the rush.",
        cta: "Explore E-commerce",
        icon: Globe,
        color: "#7C5CFC",
    },
    {
        id: "subscriptions",
        cat: "finance-commerce",
        label: "Subscriptions & Recurring",
        title: "Recurring Revenue on Rails.",
        body: "Plans, bundles and repeat billing with renewal tracking built in.",
        ai: "Flags churn risk and upcoming renewals before they lapse.",
        cta: "Explore Subscriptions",
        icon: RefreshCw,
        color: "#A855F7",
    },
    {
        id: "reporting",
        cat: "finance-commerce",
        label: "Reporting & Dashboards",
        title: "Know Your Business. Make Better Decisions.",
        body: "Live dashboards across jobs, team activity, stock and invoicing.",
        ai: "Surfaces trends and anomalies so you spot problems earlier.",
        cta: "Explore Reporting",
        icon: BarChart3,
        color: "#00B8D9",
    },
    {
        id: "automation",
        cat: "ai-intel",
        label: "Workflow Automation",
        title: "Put Routine Work on Autopilot.",
        body: "Automate routine operational workflows end to end, from assignment to approval.",
        ai: "Assigns jobs, chases approvals and keeps records complete without admin.",
        cta: "Explore Automation",
        icon: WorkflowIcon,
        color: "#D946EF",
    },
];

const CardImage = ({ mod }) => {
    const Icon = mod.icon;
    return (
        <div className="relative flex aspect-[16/10] items-center justify-center overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#0B1220] via-[#101A30] to-[#0B1220]">
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ background: `radial-gradient(circle at 70% 20%, ${mod.color}14 0%, transparent 55%)` }}
            />
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
                <Icon className="h-5 w-5" style={{ color: mod.color }} strokeWidth={1.75} />
            </div>
            <p className="absolute bottom-2.5 font-mono-brand text-[9px] uppercase tracking-[0.25em] text-[#475569]">
                Temporary placeholder
            </p>
        </div>
    );
};

const ModuleCard = ({ mod, index }) => (
    <article
        className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0D1526]/80 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#00E5FF]/30"
        data-testid={`module-card-${mod.id}`}
    >
        <CardImage mod={mod} />
        <div className="flex flex-1 flex-col p-6">
            <p className="flex items-center gap-3 font-mono-brand text-[10px] uppercase tracking-[0.25em] text-[#22D3EE]" data-testid={`module-label-${index + 1}`}>
                <span className="text-[#64748B]">{String(index + 1).padStart(2, "0")}</span>
                {mod.label}
            </p>
            <h3 className="mt-4 font-display text-xl font-bold leading-snug text-[#F8FAFF]">{mod.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#94A3B8]">{mod.body}</p>
            <p className="mt-3 flex flex-1 items-start gap-1.5 text-xs leading-relaxed">
                <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-[#A855F7]" />
                <span className="text-brand-gradient font-medium">AI: {mod.ai}</span>
            </p>
            <span className="mt-5 inline-flex cursor-pointer items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#22D3EE] transition-colors group-hover:text-[#00E5FF]">
                {mod.cta} <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
        </div>
    </article>
);

export default function Capabilities() {
    const [cat, setCat] = useState("all");
    const [page, setPage] = useState(0);

    const filtered = cat === "all" ? MODULES : MODULES.filter((m) => m.cat === cat);
    const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage = Math.min(page, pages - 1);
    const visible = filtered.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);

    const selectCat = (id) => {
        setCat(id);
        setPage(0);
    };

    return (
        <section
            className="relative overflow-hidden bg-[#0A0F1C]"
            data-testid="capabilities-section"
            aria-labelledby="capabilities-heading"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(circle at 90% 10%, rgba(0,102,255,0.08) 0%, transparent 45%), radial-gradient(circle at 5% 95%, rgba(168,85,247,0.08) 0%, transparent 45%)",
                }}
            />
            <div className="relative mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#0066FF]/30 bg-[#0066FF]/10 px-4 py-1.5"
                            data-testid="capabilities-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#93C5FD] sm:text-xs">
                                Platform Capabilities
                            </span>
                        </span>
                        <h2
                            id="capabilities-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#F8FAFF] sm:text-5xl"
                            data-testid="capabilities-heading"
                        >
                            Every Tool You Need.
                            <br />
                            <span className="text-brand-gradient">Intelligence Built In.</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                        className="flex max-w-md flex-col items-start gap-6"
                    >
                        <p className="text-base leading-relaxed text-[#94A3B8]" data-testid="capabilities-intro">
                            TedOps AI combines your people, data and processes with applied
                            artificial intelligence — helping you run a more efficient, profitable
                            and scalable business.
                        </p>
                        <button
                            type="button"
                            className="group inline-flex items-center gap-2.5 rounded-full bg-white px-6 py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-[#0B1220] transition-transform duration-300 hover:scale-[1.03]"
                            data-testid="explore-modules-button"
                        >
                            Explore All Capabilities
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
                    className="mt-14 flex flex-wrap items-center gap-3"
                    data-testid="category-filters"
                >
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => selectCat(c.id)}
                                aria-pressed={cat === c.id}
                                className={`inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 ${
                                    cat === c.id
                                        ? "bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] text-white shadow-[0_8px_24px_-8px_rgba(0,102,255,0.6)]"
                                        : "border border-[#0066FF]/30 text-[#94A3B8] hover:border-[#00E5FF]/50 hover:text-[#F8FAFF]"
                                }`}
                                data-testid={`filter-pill-${c.id}`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                {c.label}
                            </button>
                        );
                    })}
                    <div className="ml-auto hidden gap-3 lg:flex">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            aria-label="Previous modules"
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#0066FF]/30 text-[#94A3B8] transition-all duration-300 hover:border-[#00E5FF]/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                            data-testid="carousel-prev"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                            disabled={safePage === pages - 1}
                            aria-label="Next modules"
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#0066FF]/30 text-[#94A3B8] transition-all duration-300 hover:border-[#00E5FF]/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                            data-testid="carousel-next"
                        >
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </motion.div>

                <div className="mt-10">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`${cat}-${safePage}`}
                            initial={{ opacity: 0, x: 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -24 }}
                            transition={{ duration: 0.32, ease: "easeInOut" }}
                            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
                            data-testid="module-grid"
                        >
                            {visible.map((mod) => (
                                <ModuleCard key={mod.id} mod={mod} index={MODULES.indexOf(mod)} />
                            ))}
                        </motion.div>
                    </AnimatePresence>

                    <div className="mt-10 flex items-center justify-center gap-1" data-testid="carousel-dots">
                        {Array.from({ length: pages }).map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setPage(i)}
                                aria-label={`Go to page ${i + 1}`}
                                aria-current={i === safePage}
                                className="group flex h-11 w-11 items-center justify-center"
                                data-testid={`carousel-dot-${i + 1}`}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`block h-2 rounded-full transition-all duration-300 ${
                                        i === safePage
                                            ? "w-9 bg-gradient-to-r from-[#00E5FF] to-[#A855F7]"
                                            : "w-2 bg-[#334155] group-hover:bg-[#475569]"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
